// ============================================================================
// Audio playback (spec §14.1). Prefers the server-side Google Cloud TTS proxy
// (hanja-tts Edge Function) and falls back to the browser's Web Speech API when
// the function is unavailable (e.g. local dev, or not yet deployed). One shared
// <audio> element so a new play cancels the previous one.
// ============================================================================

import * as api from '@core/data/api.js';

let googleAvailable = null; // null = unknown, true/false once probed this session
let audioEl = null;

function player() {
  if (!audioEl) audioEl = new Audio();
  return audioEl;
}

function playUrl(url) {
  const a = player();
  try { a.pause(); } catch { /* ignore */ }
  a.src = url;
  return a.play().catch(() => webSpeak(currentText));
}

let currentText = '';

/** Speak Korean text: Google TTS if reachable, else Web Speech. */
export async function speak(text, opts = {}) {
  if (!text) return false;
  currentText = text;
  if (googleAvailable !== false) {
    try {
      const url = await api.synthesizeSpeech({ text, ...opts });
      googleAvailable = true;
      playUrl(url);
      return true;
    } catch {
      // Function missing/unconfigured/unauthorized → use the browser voice for
      // the rest of the session rather than retrying a call that won't work.
      googleAvailable = false;
    }
  }
  return webSpeak(text);
}

/** Browser Web Speech fallback. Returns true if it could speak. */
export function webSpeak(text) {
  try {
    if (!('speechSynthesis' in window) || !text) return false;
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'ko-KR';
    u.rate = 0.9;
    const ko = speechSynthesis.getVoices().find((v) => /ko(-|_)?KR/i.test(v.lang));
    if (ko) u.voice = ko;
    speechSynthesis.cancel();
    speechSynthesis.speak(u);
    return true;
  } catch {
    return false;
  }
}

/** A reusable 🔊 button that speaks `getText()` when clicked. */
export function speakerButton(getText, { label = '🔊', title = 'Play audio' } = {}) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'btn btn--ghost speaker-btn';
  btn.textContent = label;
  btn.title = title;
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    const t = typeof getText === 'function' ? getText() : getText;
    speak(t);
  });
  return btn;
}
